import React from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
} from '@mui/material';
import { FIELD_STYLES } from './fieldStyles';

const AutocompleteField = ({
  id,
  label,
  required = false,
  value,
  onChange,
  inputValue,
  onInputChange,
  options = [],
  placeholder,
  error,
  helperText,
  disabled = false,
  multiple = false,
  renderOption,
  getOptionLabel = (option) => option.name ?? '',
  isOptionEqualToValue = (option, val) => option.id === val.id,
  filterSelectedOptions = false,
  renderTag,
  limitTags = 5,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {label && (
        <InputLabel
          required={required}
          sx={{
            position: 'static',
            transform: 'none',
            mb: 0.75,
            '& .MuiFormLabel-asterisk': {
              color: 'error.main',
            },
          }}>
          {label}
        </InputLabel>
      )}

      <FormControl fullWidth error={!!error} disabled={disabled}>
        <Autocomplete
          id={id}
          multiple={multiple}
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => onInputChange?.(newInputValue)}
          options={options}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          filterSelectedOptions={filterSelectedOptions}
          disabled={disabled}
          limitTags={limitTags}
          renderTags={
            multiple
              ? (values, getTagProps) =>
                  values.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        {...tagProps}
                        key={
                          typeof option === 'string'
                            ? option
                            : (option.id ?? index)
                        }
                        size="small"
                        label={
                          renderTag ? renderTag(option) : getOptionLabel(option)
                        }
                        sx={{
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            padding: '4px 6px 4px 2px',
                          },
                        }}
                      />
                    );
                  })
              : undefined
          }
          renderOption={
            renderOption
              ? (props, option) => {
                  const { key, ...restProps } = props;
                  return (
                    <li
                      {...restProps}
                      key={typeof option === 'string' ? option : option.id}>
                      {renderOption(option)}
                    </li>
                  );
                }
              : undefined
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={!value?.length ? placeholder : undefined}
              error={!!error}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: FIELD_STYLES.minHeight,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  paddingRight: '56px !important',
                  gap: '4px',
                  padding: '6px 56px 6px 8px',
                },
                '& .MuiAutocomplete-tag': {
                  margin: '2px 0',
                },
                '& .MuiAutocomplete-endAdornment': {
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: '8px',
                },
              }}
            />
          )}
        />

        {(error || helperText) && (
          <FormHelperText sx={{ m: 0 }}>{error || helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default AutocompleteField;
